import { create } from 'zustand';
import { apiFetch } from '../lib/api';

/**
 * Trials individuais de módulos do tenant ([UI-ASSINATURA]).
 *
 * Fonte: GET /auth/me → campo `modulos_trial` — backend devolve só os
 * trials VIGENTES (expira_em > now()). Banner por página filtra pelo
 * `codigo` do módulo daquela tela.
 *
 * Cache de 60s: várias páginas com `ModuloTrialBanner` montadas ao mesmo
 * tempo (ex: rotas aninhadas que disparam render quase juntas) reusam o
 * mesmo snapshot e não bombardeiam o backend. `carregar()` honra esse
 * TTL — chamar várias vezes em sequência é no-op até o TTL vencer.
 *
 * Por que store separado de `useAuthStore`: o auth store hoje guarda só o
 * payload do login. Não queremos misturar dados voláteis (countdown muda
 * a cada minuto) com sessão.
 */

export interface ModuloTrial {
  modulo_id: number;
  codigo: string;
  nome: string;
  trial_expira_em: string;
  dias_restantes: number;
}

const TTL_MS = 60_000;

interface ModulosTrialState {
  modulos_trial: ModuloTrial[];
  ultimo_fetch: number | null;
  carregando: boolean;
  /** Força refetch ignorando o TTL. */
  carregar: (opts?: { forcar?: boolean }) => Promise<void>;
  limpar: () => void;
  /** Retorna o trial do módulo informado ou null se não há trial vivo. */
  trialDe: (codigo: string) => ModuloTrial | null;
}

export const useModulosTrialStore = create<ModulosTrialState>((set, get) => ({
  modulos_trial: [],
  ultimo_fetch: null,
  carregando: false,
  carregar: async ({ forcar } = {}) => {
    const { ultimo_fetch, carregando } = get();
    if (carregando) return;
    if (
      !forcar &&
      ultimo_fetch !== null &&
      Date.now() - ultimo_fetch < TTL_MS
    ) {
      return;
    }
    set({ carregando: true });
    try {
      const res = await apiFetch('/auth/me');
      if (!res.ok) {
        set({ carregando: false });
        return;
      }
      const data = (await res.json()) as { modulos_trial?: ModuloTrial[] };
      set({
        modulos_trial: data.modulos_trial ?? [],
        ultimo_fetch: Date.now(),
        carregando: false,
      });
    } catch {
      set({ carregando: false });
    }
  },
  limpar: () =>
    set({ modulos_trial: [], ultimo_fetch: null, carregando: false }),
  trialDe: (codigo) =>
    get().modulos_trial.find((m) => m.codigo === codigo) ?? null,
}));
